/**
 * Adapted from PlayCanvas Engine's gsplat-params.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import * as THREE from 'three'
import {
  areGSplatClipStatesEqual,
  createEmptyGSplatClipPacket,
  createGSplatClipPacket,
  createGSplatClipState,
  GSPLAT_MAX_CLIP_VOLUMES,
  type GSplatClipPacket,
  type GSplatClipState,
  updateGSplatClipPacket,
} from '@/plugins/gsplat/clipping/GSplatClipState'
import { GSplatMaterialTemplate } from '@/plugins/gsplat/effects/GSplatMaterialTemplate'
import type {
  GSplatGraphicsAdapter,
  GSplatMaterialLeaf,
  GSplatTextureFormat,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSplatFormat, type GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import { ClipTask } from '@/shared/constants'
import { getLoggerManager } from '@/shared/logging'
import type { ClipVolume, Disposable } from '@/shared/types/core'
import type { GSplatLodRefinementMode } from '@/shared/types/gsplat'
import type {
  GSplatFormatSelectionParticipant,
  GSplatFormatSelectionStage,
  GSplatRendererSelectionParticipant,
  GSplatRendererSelectionStage,
} from './GSplatManager'
import {
  type GSplatRendererRequest,
  type GSplatResolvedRenderer,
  reportGSplatSortingFallbackOnce,
} from './GSplatPipelineTypes'
import { GSplatVaryings } from './GSplatVaryings'
import {
  GSPLAT_COMPACT_READ_GLSL,
  GSPLAT_COMPACT_READ_WGSL,
  GSPLAT_COMPACT_WRITE_GLSL,
  GSPLAT_COMPACT_WRITE_WGSL,
  GSPLAT_LARGE_READ_GLSL,
  GSPLAT_LARGE_READ_WGSL,
  GSPLAT_LARGE_WRITE_GLSL,
  GSPLAT_LARGE_WRITE_WGSL,
} from './shaders/gsplatWorkBufferFormatChunks'

const log = getLoggerManager().getLogger('gsplat:params')

export const GSPLAT_DATA_FORMAT_COMPACT = 'compact'
export const GSPLAT_DATA_FORMAT_LARGE = 'large'

export type GSplatDataFormat = typeof GSPLAT_DATA_FORMAT_COMPACT | typeof GSPLAT_DATA_FORMAT_LARGE
export type GSplatRendererSetterInput = GSplatRendererRequest | 'compute'
export type GSplatDebugMode = 'none' | 'lod' | 'sh-update' | 'aabbs' | 'node-aabbs'

export interface GSplatSceneRenderSettings {
  readonly gsplatRadialSorting?: boolean | null
  readonly gsplatLodUpdateDistance?: number | null
  readonly gsplatLodUpdateAngle?: number | null
  readonly gsplatLodBehindPenalty?: number | null
  readonly gsplatLodUnderfillLimit?: number | null
  readonly gsplatLodRefinementMode?: GSplatLodRefinementMode | null
  readonly gsplatSplatBudget?: number | null
  readonly gsplatAlphaClip?: number | null
  readonly gsplatAlphaClipForward?: number | null
  readonly gsplatMinPixelSize?: number | null
  readonly gsplatMinContribution?: number | null
  readonly gsplatFoveationStrength?: number | null
  readonly gsplatFoveationCenter?: number | null
  readonly gsplatAntiAlias?: boolean | null
  readonly gsplatUseFog?: boolean | null
  readonly gsplatColorUpdateAngle?: number | null
  readonly gsplatCooldownTicks?: number | null
  readonly gsplatDataFormat?: string | null
  readonly gsplatEnableIds?: boolean | null
}

export interface GSplatParamsFrameCompletion {
  readonly managerCount: number
  readonly synchronizedManagerCount: number
  readonly renderSynchronized?: boolean
  readonly aborted?: boolean
}

const MATERIAL_DEFAULTS = Object.freeze([
  ['alphaClip', 0.3],
  ['alphaClipForward', 1 / 255],
  ['minPixelSize', 2],
  ['minContribution', 3],
  ['foveationStrength', 0],
  ['foveationCenter', 0.3],
] as const)

let computeRemovedDiagnosticEmitted = false
const compatibilityDiagnostics = new Set<string>()

function rollbackStages(stages: readonly { rollback(): void }[]): void {
  for (let index = stages.length - 1; index >= 0; index--) {
    try {
      stages[index].rollback()
    } catch {
      // Preserve the original stage/commit error. Each stage still performs best-effort cleanup.
    }
  }
}

/** 全部 Unified Manager 共用的 format、material、varyings 与 renderer selection owner。 */
function hasGSplatCullingVolumes(state: GSplatClipState): boolean {
  return state.volumes.some(
    (volume) =>
      volume.clipTask === ClipTask.SHOW_INSIDE || volume.clipTask === ClipTask.SHOW_OUTSIDE
  )
}

function maxClipPolygonVertices(state: GSplatClipState): number {
  return state.polygons.reduce((maximum, polygon) => Math.max(maximum, polygon.vertices.length), 0)
}

function normalizeClipVertexCapacity(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0
  return 2 ** Math.ceil(Math.log2(Math.max(1, Math.trunc(value))))
}

export class GSplatParams implements Disposable {
  readonly adapter: GSplatGraphicsAdapter
  dirty = false
  radialSorting = false
  lodUpdateDistance = 1
  lodUpdateAngle = 0
  colorRampIntensity = 1
  useFog = true
  colorUpdateAngle = 10
  cooldownTicks = 100

  private readonly templateMaterial: GSplatMaterialLeaf
  private readonly logicalMaterialTemplate: GSplatMaterialTemplate
  private readonly varyingRegistry: GSplatVaryings
  private currentFormat: GSplatFormat
  private currentDataFormat: string = GSPLAT_DATA_FORMAT_COMPACT
  private requestedRenderer: GSplatRendererRequest = 'auto'
  private resolvedRenderer: GSplatResolvedRenderer
  private currentDebug: GSplatDebugMode = 'none'
  private idsEnabled = false
  private lodBehindPenaltyValue = 1
  private lodUnderfillLimitValue = 0
  private lodRefinementModeValue: GSplatLodRefinementMode = 'step'
  private splatBudgetValue = 0
  private colorRampValue: unknown | null = null
  private fisheyeValue = 0
  private clipStateValue: GSplatClipState = createGSplatClipState([])
  private clipPacketValue: GSplatClipPacket | null = null
  private clipPrewarmMaxVertices = 0
  private clipPrewarmMaxVolumes = 0
  private appliedVaryingsVersion = 0
  private disposed = false

  constructor(adapter: GSplatGraphicsAdapter) {
    this.adapter = adapter
    this.logicalMaterialTemplate = new GSplatMaterialTemplate()
    let material: GSplatMaterialLeaf | undefined
    try {
      material = adapter.createMaterial({
        label: 'gsplat-params-template',
        language: adapter.language,
        vertexSource: '',
        fragmentSource: '',
      })
      this.resolvedRenderer = this.resolveRenderer(this.requestedRenderer)
      this.currentFormat = this.createFormat(GSPLAT_DATA_FORMAT_COMPACT)
      this.varyingRegistry = new GSplatVaryings()
      for (const [name, value] of MATERIAL_DEFAULTS) material.setParameter(name, value)
      this.templateMaterial = material
      this.syncMaterialTemplate()
    } catch (error) {
      this.logicalMaterialTemplate.dispose()
      material?.dispose()
      throw error
    }
  }

  get renderer(): GSplatRendererRequest {
    return this.requestedRenderer
  }

  get currentRenderer(): GSplatResolvedRenderer {
    return this.resolvedRenderer
  }

  get dataFormat(): string {
    return this.currentDataFormat
  }

  get format(): GSplatFormat {
    return this.currentFormat
  }

  get material(): GSplatMaterialLeaf {
    return this.templateMaterial
  }

  get materialTemplate(): GSplatMaterialTemplate {
    return this.logicalMaterialTemplate
  }

  get clipState(): GSplatClipState {
    return this.clipStateValue
  }

  /** 提前安装零 volume 的裁切变体，避免首次框选结束才编译 clip shader。 */
  prewarmClip(maxVertices: number, maxVolumes = 1): boolean {
    this.assertActive()
    const volumeCapacity = Math.min(
      GSPLAT_MAX_CLIP_VOLUMES,
      Math.max(1, Number.isFinite(maxVolumes) ? Math.trunc(maxVolumes) : 1)
    )
    const vertexCapacity = normalizeClipVertexCapacity(maxVertices)
    const alreadyEnabled = Boolean(this.templateMaterial.getDefine('GSPLAT_CLIP_ENABLED'))
    if (
      alreadyEnabled &&
      this.clipPrewarmMaxVolumes >= volumeCapacity &&
      this.clipPrewarmMaxVertices >= vertexCapacity
    ) {
      return false
    }

    this.clipPrewarmMaxVolumes = Math.max(this.clipPrewarmMaxVolumes, volumeCapacity)
    this.clipPrewarmMaxVertices = Math.max(this.clipPrewarmMaxVertices, vertexCapacity)

    let packet = this.clipPacketValue
    if (!packet) {
      packet = createEmptyGSplatClipPacket(new THREE.Matrix4())
      this.clipPacketValue = packet
    } else if (this.clipStateValue.volumes.length === 0) {
      updateGSplatClipPacket(packet, this.clipStateValue, new THREE.Matrix4())
    }

    this.applyClipMaterialParameters(
      packet,
      this.clipPrewarmMaxVolumes,
      this.clipPrewarmMaxVertices
    )
    this.templateMaterial.update()
    this.syncMaterialTemplate()
    return true
  }

  /** 工具退出时释放常驻预编译状态；已有确认裁切体时保留实际裁切。 */
  releaseClipPrewarm(): boolean {
    this.assertActive()
    if (this.clipPrewarmMaxVolumes === 0 && this.clipPrewarmMaxVertices === 0) {
      return false
    }

    this.clipPrewarmMaxVolumes = 0
    this.clipPrewarmMaxVertices = 0
    if (this.clipStateValue.volumes.length > 0 && this.clipPacketValue) {
      this.applyClipMaterialParameters(
        this.clipPacketValue,
        this.clipStateValue.volumes.length,
        maxClipPolygonVertices(this.clipStateValue)
      )
    } else {
      const packet = this.clipPacketValue
      this.clipPacketValue = null
      this.templateMaterial.setDefine('GSPLAT_CLIP_ENABLED', undefined)
      this.templateMaterial.deleteParameter('gsplatClipData')
      this.templateMaterial.deleteParameter('gsplatClipPolygonCount')
      this.templateMaterial.deleteParameter('gsplatClipMaxVertices')
      this.templateMaterial.deleteParameter('gsplatClipWorldOffset')
      packet?.texture.dispose()
    }
    this.templateMaterial.update()
    this.syncMaterialTemplate()
    return true
  }

  private applyClipMaterialParameters(
    packet: GSplatClipPacket,
    volumeCapacity: number,
    vertexCapacity: number
  ): void {
    this.templateMaterial.setDefine('GSPLAT_CLIP_ENABLED', true)
    this.templateMaterial.setParameter('gsplatClipData', packet.texture)
    this.templateMaterial.setParameter('gsplatClipPolygonCount', volumeCapacity)
    this.templateMaterial.setParameter('gsplatClipMaxVertices', vertexCapacity)
    this.templateMaterial.setParameter('gsplatClipWorldOffset', new THREE.Vector3())
  }

  /**
   * Unified WorkBuffer 已把所有 Placement 烘焙到同一世界坐标空间，因此这里只维护一份
   * system-wide 裁切纹理。各 View 的 sceneOrigin 偏移由 Renderer 在提交前单独补入。
   */
  setClipVolumes(volumes: ReadonlyArray<ClipVolume>): boolean {
    this.assertActive()
    const previousState = this.clipStateValue
    const nextState = createGSplatClipState(volumes)
    if (areGSplatClipStatesEqual(previousState, nextState)) return false

    const previousPacket = this.clipPacketValue
    this.clipStateValue = nextState
    const actualMaxVertices = maxClipPolygonVertices(nextState)
    if (this.clipPrewarmMaxVolumes > 0) {
      this.clipPrewarmMaxVolumes = Math.max(this.clipPrewarmMaxVolumes, nextState.volumes.length)
      this.clipPrewarmMaxVertices = Math.max(this.clipPrewarmMaxVertices, actualMaxVertices)
    }

    if (nextState.volumes.length > 0) {
      const nextPacket = previousPacket ?? createGSplatClipPacket(nextState, new THREE.Matrix4())
      if (!nextPacket) return false
      if (previousPacket) {
        updateGSplatClipPacket(previousPacket, nextState, new THREE.Matrix4())
      }
      this.clipPacketValue = nextPacket
      this.applyClipMaterialParameters(
        nextPacket,
        this.clipPrewarmMaxVolumes > 0 ? this.clipPrewarmMaxVolumes : nextState.volumes.length,
        this.clipPrewarmMaxVertices > 0 ? this.clipPrewarmMaxVertices : actualMaxVertices
      )
    } else if (this.clipPrewarmMaxVolumes > 0) {
      const nextPacket = previousPacket ?? createEmptyGSplatClipPacket(new THREE.Matrix4())
      updateGSplatClipPacket(nextPacket, nextState, new THREE.Matrix4())
      this.clipPacketValue = nextPacket
      this.applyClipMaterialParameters(
        nextPacket,
        this.clipPrewarmMaxVolumes,
        this.clipPrewarmMaxVertices
      )
    } else {
      this.clipPacketValue = null
      this.templateMaterial.setDefine('GSPLAT_CLIP_ENABLED', undefined)
      this.templateMaterial.deleteParameter('gsplatClipData')
      this.templateMaterial.deleteParameter('gsplatClipPolygonCount')
      this.templateMaterial.deleteParameter('gsplatClipMaxVertices')
      this.templateMaterial.deleteParameter('gsplatClipWorldOffset')
      previousPacket?.texture.dispose()
    }
    this.templateMaterial.update()
    this.syncMaterialTemplate()
    const cullingChanged =
      hasGSplatCullingVolumes(previousState) || hasGSplatCullingVolumes(nextState)
    this.dirty ||= cullingChanged
    return true
  }

  get varyings(): GSplatVaryings {
    return this.varyingRegistry
  }

  get debug(): GSplatDebugMode {
    return this.currentDebug
  }

  set debug(value: GSplatDebugMode | 'heatmap') {
    if (value === 'heatmap') {
      this.warnCompatibilityOnce('debug:heatmap', 'GSplatParams.debug: HEATMAP 已移除并被忽略')
      return
    }
    if (value === this.currentDebug) return
    const previous = this.currentDebug
    this.currentDebug = value
    if (value === 'lod' || previous === 'lod') this.dirty = true
  }

  get colorizeLod(): boolean {
    this.warnCompatibilityOnce('colorizeLod', 'GSplatParams.colorizeLod 已弃用，请使用 debug=lod')
    return this.currentDebug === 'lod'
  }

  set colorizeLod(value: boolean) {
    this.warnCompatibilityOnce('colorizeLod', 'GSplatParams.colorizeLod 已弃用，请使用 debug=lod')
    this.debug = value ? 'lod' : 'none'
  }

  get debugAabbs(): boolean {
    this.warnCompatibilityOnce('debugAabbs', 'GSplatParams.debugAabbs 已弃用，请使用 debug=aabbs')
    return this.currentDebug === 'aabbs'
  }

  set debugAabbs(value: boolean) {
    this.warnCompatibilityOnce('debugAabbs', 'GSplatParams.debugAabbs 已弃用，请使用 debug=aabbs')
    this.debug = value ? 'aabbs' : 'none'
  }

  get debugNodeAabbs(): boolean {
    this.warnCompatibilityOnce(
      'debugNodeAabbs',
      'GSplatParams.debugNodeAabbs 已弃用，请使用 debug=node-aabbs'
    )
    return this.currentDebug === 'node-aabbs'
  }

  set debugNodeAabbs(value: boolean) {
    this.warnCompatibilityOnce(
      'debugNodeAabbs',
      'GSplatParams.debugNodeAabbs 已弃用，请使用 debug=node-aabbs'
    )
    this.debug = value ? 'node-aabbs' : 'none'
  }

  get colorizeColorUpdate(): boolean {
    this.warnCompatibilityOnce(
      'colorizeColorUpdate',
      'GSplatParams.colorizeColorUpdate 已弃用，请使用 debug=sh-update'
    )
    return this.currentDebug === 'sh-update'
  }

  set colorizeColorUpdate(value: boolean) {
    this.warnCompatibilityOnce(
      'colorizeColorUpdate',
      'GSplatParams.colorizeColorUpdate 已弃用，请使用 debug=sh-update'
    )
    this.debug = value ? 'sh-update' : 'none'
  }

  get enableIds(): boolean {
    return this.idsEnabled
  }

  set enableIds(value: boolean) {
    if (value === this.idsEnabled) return
    if (value) {
      if (!this.currentFormat.getStream('pcId')) {
        this.currentFormat.addExtraStreams([{ name: 'pcId', format: 'r32uint' }])
      }
      this.idsEnabled = true
      return
    }
    this.currentFormat.removeExtraStreams(['pcId'])
    this.idsEnabled = false
  }

  get lodBehindPenalty(): number {
    return this.lodBehindPenaltyValue
  }

  set lodBehindPenalty(value: number) {
    if (value === this.lodBehindPenaltyValue) return
    this.lodBehindPenaltyValue = value
    this.dirty = true
  }

  get lodUnderfillLimit(): number {
    return this.lodUnderfillLimitValue
  }

  set lodUnderfillLimit(value: number) {
    if (value === this.lodUnderfillLimitValue) return
    this.lodUnderfillLimitValue = value
    this.dirty = true
  }

  get lodRefinementMode(): GSplatLodRefinementMode {
    return this.lodRefinementModeValue
  }

  set lodRefinementMode(value: GSplatLodRefinementMode) {
    if (value !== 'step' && value !== 'direct') {
      throw new Error(`非法 GSplat lodRefinementMode: ${value}`)
    }
    if (value === this.lodRefinementModeValue) return
    this.lodRefinementModeValue = value
    this.dirty = true
  }

  get splatBudget(): number {
    return this.splatBudgetValue
  }

  set splatBudget(value: number) {
    if (value === this.splatBudgetValue) return
    this.splatBudgetValue = value
    this.dirty = true
  }

  get lodRangeMin(): number {
    this.warnCompatibilityOnce(
      'lodRangeMin',
      'GSplatParams.lodRangeMin 已弃用，请在 GSplat component 上设置'
    )
    return 0
  }

  set lodRangeMin(_value: number) {
    this.warnCompatibilityOnce(
      'lodRangeMin',
      'GSplatParams.lodRangeMin 已弃用，请在 GSplat component 上设置'
    )
  }

  get lodRangeMax(): number {
    this.warnCompatibilityOnce(
      'lodRangeMax',
      'GSplatParams.lodRangeMax 已弃用，请在 GSplat component 上设置'
    )
    return 99
  }

  set lodRangeMax(_value: number) {
    this.warnCompatibilityOnce(
      'lodRangeMax',
      'GSplatParams.lodRangeMax 已弃用，请在 GSplat component 上设置'
    )
  }

  get colorRamp(): unknown | null {
    return this.colorRampValue
  }

  set colorRamp(value: unknown | null) {
    if (value === this.colorRampValue) return
    this.colorRampValue = value
    this.dirty = true
  }

  get alphaClip(): number {
    return this.readMaterialNumber('alphaClip', 0.3)
  }

  set alphaClip(value: number) {
    this.setMaterialParameter('alphaClip', value)
  }

  get alphaClipForward(): number {
    return this.readMaterialNumber('alphaClipForward', 1 / 255)
  }

  set alphaClipForward(value: number) {
    this.setMaterialParameter('alphaClipForward', value)
  }

  get minPixelSize(): number {
    return this.readMaterialNumber('minPixelSize', 2)
  }

  set minPixelSize(value: number) {
    this.setMaterialParameter('minPixelSize', value)
  }

  get minContribution(): number {
    return this.readMaterialNumber('minContribution', 3)
  }

  set minContribution(value: number) {
    this.setMaterialParameter('minContribution', value)
  }

  get foveationStrength(): number {
    return this.readMaterialNumber('foveationStrength', 0)
  }

  set foveationStrength(value: number) {
    this.setMaterialParameter('foveationStrength', value)
  }

  get foveationCenter(): number {
    return this.readMaterialNumber('foveationCenter', 0.3)
  }

  set foveationCenter(value: number) {
    this.setMaterialParameter('foveationCenter', value)
  }

  get antiAlias(): boolean {
    return Boolean(this.templateMaterial.getDefine('GSPLAT_AA'))
  }

  set antiAlias(value: boolean) {
    this.templateMaterial.setDefine('GSPLAT_AA', value)
    this.templateMaterial.update()
  }

  get twoDimensional(): boolean {
    return Boolean(this.templateMaterial.getDefine('GSPLAT_2DGS'))
  }

  set twoDimensional(value: boolean) {
    this.templateMaterial.setDefine('GSPLAT_2DGS', value)
    this.templateMaterial.update()
  }

  get fisheye(): number {
    return this.fisheyeValue
  }

  set fisheye(value: number) {
    if (value === this.fisheyeValue) return
    const wasEnabled = this.fisheyeValue > 0
    this.fisheyeValue = value
    const isEnabled = value > 0
    if (wasEnabled === isEnabled) return
    this.templateMaterial.setDefine('GSPLAT_FISHEYE', isEnabled)
    this.templateMaterial.update()
  }

  get colorUpdateDistance(): number {
    this.warnCompatibilityOnce(
      'colorUpdateDistance',
      'GSplatParams.colorUpdateDistance 已移除，请使用 colorUpdateAngle'
    )
    return 0
  }

  set colorUpdateDistance(_value: number) {
    this.warnCompatibilityOnce(
      'colorUpdateDistance',
      'GSplatParams.colorUpdateDistance 已移除，请使用 colorUpdateAngle'
    )
  }

  get colorUpdateDistanceLodScale(): number {
    this.warnCompatibilityOnce(
      'colorUpdateDistanceLodScale',
      'GSplatParams.colorUpdateDistanceLodScale 已移除，节点距离缩放已自动处理'
    )
    return 0
  }

  set colorUpdateDistanceLodScale(_value: number) {
    this.warnCompatibilityOnce(
      'colorUpdateDistanceLodScale',
      'GSplatParams.colorUpdateDistanceLodScale 已移除，节点距离缩放已自动处理'
    )
  }

  get colorUpdateAngleLodScale(): number {
    this.warnCompatibilityOnce(
      'colorUpdateAngleLodScale',
      'GSplatParams.colorUpdateAngleLodScale 已移除，节点距离缩放已自动处理'
    )
    return 0
  }

  set colorUpdateAngleLodScale(_value: number) {
    this.warnCompatibilityOnce(
      'colorUpdateAngleLodScale',
      'GSplatParams.colorUpdateAngleLodScale 已移除，节点距离缩放已自动处理'
    )
  }

  resolveRenderer(requested: GSplatRendererRequest): GSplatResolvedRenderer {
    if (requested === 'auto') return this.adapter.backend === 'webgpu' ? 'gpu' : 'cpu'
    if (requested === 'gpu' && this.adapter.backend !== 'webgpu') return 'cpu'
    return requested
  }

  /**
   * 供 GSplatSystem 的 system-wide transaction 调用。
   *
   * 所有 Manager 先 stage；全部 commit 后才发布 Params requested/resolved 字段，最后才释放旧资源。
   */
  setRendererSelection(
    input: GSplatRendererSetterInput,
    managers: readonly GSplatRendererSelectionParticipant[]
  ): boolean {
    this.assertActive()
    const requested = this.normalizeRenderer(input)
    const resolved = this.resolveRenderer(requested)
    if (requested === this.requestedRenderer && resolved === this.resolvedRenderer) return false

    const stages: GSplatRendererSelectionStage[] = []
    try {
      for (const manager of managers) stages.push(manager.stageRendererSelection(resolved))
    } catch (error) {
      rollbackStages(stages)
      throw error
    }

    try {
      for (const stage of stages) stage.commit()
    } catch (error) {
      rollbackStages(stages)
      throw error
    }

    this.requestedRenderer = requested
    this.resolvedRenderer = resolved
    reportGSplatSortingFallbackOnce(this.adapter.backend, requested, resolved)

    let firstFinalizeError: unknown
    for (const stage of stages) {
      try {
        stage.finalize()
      } catch (error) {
        firstFinalizeError ??= error
      }
    }
    if (firstFinalizeError) throw firstFinalizeError
    return true
  }

  setDataFormatSelection(
    dataFormat: string,
    managers: readonly GSplatFormatSelectionParticipant[]
  ): boolean {
    this.assertActive()
    if (dataFormat === this.currentDataFormat) return false

    const extraStreams = this.currentFormat.extraStreams.map((stream) => ({
      name: stream.name,
      format: stream.format,
      storage: stream.storage,
    }))
    const stagedFormat = this.createFormat(dataFormat)
    if (extraStreams.length > 0) stagedFormat.addExtraStreams(extraStreams)

    const stages: GSplatFormatSelectionStage[] = []
    try {
      for (const manager of managers) stages.push(manager.stageFormatSelection(stagedFormat))
    } catch (error) {
      rollbackStages(stages)
      throw error
    }

    try {
      for (const stage of stages) stage.commit()
    } catch (error) {
      rollbackStages(stages)
      throw error
    }

    this.currentDataFormat = dataFormat
    this.currentFormat = stagedFormat
    this.dirty = true

    let firstFinalizeError: unknown
    for (const stage of stages) {
      try {
        stage.finalize()
      } catch (error) {
        firstFinalizeError ??= error
      }
    }
    if (firstFinalizeError) throw firstFinalizeError
    return true
  }

  /**
   * Atomically add permanent extra streams to the current format identity.
   *
   * Trees/other WorkBuffer features use this path instead of mutating
   * `params.format` directly, so every live Manager stages the same layout
   * before the format revision becomes visible.
   */
  addExtraStreamsSelection(
    streams: readonly GSplatStreamDescriptor[],
    managers: readonly GSplatFormatSelectionParticipant[]
  ): boolean {
    this.assertActive()
    if (streams.length === 0) return false

    const additions: GSplatStreamDescriptor[] = []
    const stagedNames = new Set<string>()
    for (const stream of streams) {
      const existing = this.currentFormat.getStream(stream.name)
      if (existing) {
        if (
          existing.format !== stream.format ||
          (existing.storage ?? 'resource') !== (stream.storage ?? 'resource')
        ) {
          throw new Error(
            `GSplat extra stream "${stream.name}" 布局冲突: ` +
              `existing=${existing.format}/${existing.storage ?? 'resource'}, ` +
              `requested=${stream.format}/${stream.storage ?? 'resource'}`
          )
        }
        continue
      }
      if (stagedNames.has(stream.name)) {
        throw new Error(`GSplat extra stream "${stream.name}" 在请求中重复`)
      }
      stagedNames.add(stream.name)
      additions.push(stream)
    }
    if (additions.length === 0) return false

    const stagedFormat = this.createFormat(this.currentDataFormat)
    stagedFormat.addExtraStreams([
      ...this.currentFormat.extraStreams.map((stream) => ({
        name: stream.name,
        format: stream.format,
        storage: stream.storage,
      })),
      ...additions,
    ])

    const stages: GSplatFormatSelectionStage[] = []
    try {
      for (const manager of managers) stages.push(manager.stageFormatSelection(stagedFormat))
    } catch (error) {
      rollbackStages(stages)
      throw error
    }

    try {
      for (const stage of stages) stage.commit()
    } catch (error) {
      rollbackStages(stages)
      throw error
    }

    this.currentFormat = stagedFormat
    this.dirty = true

    let firstFinalizeError: unknown
    for (const stage of stages) {
      try {
        stage.finalize()
      } catch (error) {
        firstFinalizeError ??= error
      }
    }
    if (firstFinalizeError) throw firstFinalizeError
    return true
  }

  /** 按冻结顺序应用 scene render settings。 */
  applySettings(
    render: Readonly<GSplatSceneRenderSettings>,
    managers: readonly GSplatFormatSelectionParticipant[] = []
  ): void {
    this.assertActive()
    const radialSorting = render.gsplatRadialSorting
    const lodUpdateDistance = render.gsplatLodUpdateDistance
    const lodUpdateAngle = render.gsplatLodUpdateAngle
    const lodBehindPenalty = render.gsplatLodBehindPenalty
    const lodUnderfillLimit = render.gsplatLodUnderfillLimit
    const lodRefinementMode = render.gsplatLodRefinementMode
    const splatBudget = render.gsplatSplatBudget
    const alphaClip = render.gsplatAlphaClip
    const alphaClipForward = render.gsplatAlphaClipForward
    const minPixelSize = render.gsplatMinPixelSize
    const minContribution = render.gsplatMinContribution
    const foveationStrength = render.gsplatFoveationStrength
    const foveationCenter = render.gsplatFoveationCenter
    const antiAlias = render.gsplatAntiAlias
    const useFog = render.gsplatUseFog
    const colorUpdateAngle = render.gsplatColorUpdateAngle
    const cooldownTicks = render.gsplatCooldownTicks
    const requestedDataFormat = render.gsplatDataFormat
    const enableIds = render.gsplatEnableIds
    if (
      requestedDataFormat != null &&
      requestedDataFormat !== GSPLAT_DATA_FORMAT_COMPACT &&
      requestedDataFormat !== GSPLAT_DATA_FORMAT_LARGE
    ) {
      throw new Error(`不支持的公开 GSplat dataFormat: "${requestedDataFormat}"`)
    }

    this.radialSorting = radialSorting ?? this.radialSorting

    this.lodUpdateDistance = lodUpdateDistance ?? this.lodUpdateDistance
    this.lodUpdateAngle = lodUpdateAngle ?? this.lodUpdateAngle
    this.lodBehindPenalty = lodBehindPenalty ?? this.lodBehindPenalty
    this.lodUnderfillLimit = lodUnderfillLimit ?? this.lodUnderfillLimit
    this.lodRefinementMode = lodRefinementMode ?? this.lodRefinementMode
    this.splatBudget = splatBudget ?? this.splatBudget

    this.alphaClip = alphaClip ?? this.alphaClip
    this.alphaClipForward = alphaClipForward ?? this.alphaClipForward
    this.minPixelSize = minPixelSize ?? this.minPixelSize
    this.minContribution = minContribution ?? this.minContribution
    this.foveationStrength = foveationStrength ?? this.foveationStrength
    this.foveationCenter = foveationCenter ?? this.foveationCenter

    this.antiAlias = antiAlias ?? this.antiAlias
    this.useFog = useFog ?? this.useFog
    this.colorUpdateAngle = colorUpdateAngle ?? this.colorUpdateAngle
    this.cooldownTicks = cooldownTicks ?? this.cooldownTicks
    this.setDataFormatSelection(requestedDataFormat ?? this.dataFormat, managers)
    this.enableIds = enableIds ?? this.enableIds
  }

  frameUpdate(): boolean {
    this.assertActive()
    let changed = false
    if (this.appliedVaryingsVersion !== this.varyingRegistry.version) {
      this.varyingRegistry.apply(this.templateMaterial, this.adapter.language)
      this.appliedVaryingsVersion = this.varyingRegistry.version
      changed = true
    }
    return this.syncMaterialTemplate() || changed
  }

  frameEnd(completion?: Readonly<GSplatParamsFrameCompletion>): boolean {
    this.assertActive()
    if (completion) {
      const { managerCount, synchronizedManagerCount } = completion
      if (
        !Number.isSafeInteger(managerCount) ||
        !Number.isSafeInteger(synchronizedManagerCount) ||
        managerCount < 0 ||
        synchronizedManagerCount < 0 ||
        synchronizedManagerCount > managerCount
      ) {
        throw new Error(`非法 GSplat frame completion: ${synchronizedManagerCount}/${managerCount}`)
      }
      if (
        completion.aborted ||
        completion.renderSynchronized === false ||
        managerCount === 0 ||
        synchronizedManagerCount !== managerCount
      ) {
        return false
      }
    }
    this.dirty = false
    return true
  }

  /** 上游 `_createFormat` 的 backend-neutral 适配；未知 core 值有意进入 large 分支。 */
  createFormat(dataFormat: string): GSplatFormat {
    this.assertActive()
    if (dataFormat === GSPLAT_DATA_FORMAT_COMPACT) {
      return new GSplatFormat(
        this.adapter,
        [
          { name: 'dataColor', format: 'r32uint' },
          { name: 'dataTransformA', format: 'rgba32uint' },
          { name: 'dataTransformB', format: 'r32uint' },
        ],
        {
          readGLSL: GSPLAT_COMPACT_READ_GLSL,
          readWGSL: GSPLAT_COMPACT_READ_WGSL,
          writeGLSL: GSPLAT_COMPACT_WRITE_GLSL,
          writeWGSL: GSPLAT_COMPACT_WRITE_WGSL,
          allowStreamRemoval: true,
          dataFormat,
        }
      )
    }

    const colorFormat: GSplatTextureFormat = this.adapter.getTextureFormatInfo('rgba16float')
      .renderable
      ? 'rgba16float'
      : 'rgba16uint'
    return new GSplatFormat(
      this.adapter,
      [
        { name: 'dataColor', format: colorFormat },
        { name: 'dataTransformA', format: 'rgba32uint' },
        { name: 'dataTransformB', format: 'rg32uint' },
      ],
      {
        readGLSL: GSPLAT_LARGE_READ_GLSL,
        readWGSL: GSPLAT_LARGE_READ_WGSL,
        writeGLSL: GSPLAT_LARGE_WRITE_GLSL,
        writeWGSL: GSPLAT_LARGE_WRITE_WGSL,
        allowStreamRemoval: true,
        dataFormat,
      }
    )
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.clipPacketValue?.texture.dispose()
    this.clipPacketValue = null
    this.logicalMaterialTemplate.dispose()
    this.templateMaterial.dispose()
  }

  private normalizeRenderer(input: GSplatRendererSetterInput): GSplatRendererRequest {
    if (input !== 'compute') return input
    if (!computeRemovedDiagnosticEmitted) {
      computeRemovedDiagnosticEmitted = true
      log.warn('GSplatParams.renderer: COMPUTE 已移除，回退为 AUTO')
    }
    return 'auto'
  }

  private readMaterialNumber(name: string, fallback: number): number {
    const parameter = this.templateMaterial.getParameter(name)
    if (typeof parameter === 'number') return parameter
    if (
      parameter !== null &&
      typeof parameter === 'object' &&
      'data' in parameter &&
      typeof parameter.data === 'number'
    ) {
      return parameter.data
    }
    return fallback
  }

  private setMaterialParameter(name: string, value: number): void {
    this.assertActive()
    this.templateMaterial.setParameter(name, value)
    this.templateMaterial.update()
  }

  syncMaterialTemplate(): boolean {
    this.assertActive()
    const snapshot = this.templateMaterial.captureSnapshot(this.adapter.language)
    return this.logicalMaterialTemplate.replace({
      defines: snapshot.defines,
      parameters: snapshot.parameters,
      chunks: snapshot.shaderChunks,
    })
  }

  private warnCompatibilityOnce(key: string, message: string): void {
    if (compatibilityDiagnostics.has(key)) return
    compatibilityDiagnostics.add(key)
    log.warn(message)
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('GSplatParams 已销毁')
  }
}
